import { Component, inject } from '@angular/core';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import {
  DateRange,
  DictionaryEntryModel,
  DictionaryKey,
  extractDateRange,
  extractDefaultStoreId, extractDefaultStoreIds,
  extractNumber, extractNumberArr,
  HxDictionaryService,
  HxReportService,
  HxUiLabelPipe,
  isoDate,
  ProfitLossStatementModel,
  StoreBasicModel,
  toRouterQueryParams
} from 'hx-services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { saveAs } from 'file-saver';
import { HcToastrService } from '@manager-app/service/toastr.service';

@Component({
  selector: 'app-profit-loss-statement',
  templateUrl: './profit-loss-statement.component.html',
  imports: [
    HxDateRangeSelectComponent,
    HxStoreSelectComponent,
    TranslocoPipe,
    FormsModule,
    NgIf,
    NgFor,
    DecimalPipe,
    NgClass,
    HxUiLabelPipe,
  ],
  styleUrls: ['./profit-loss-statement.component.css']
})
export class ProfitLossStatementComponent {
  params: {
    storeIds?: number[],
    date?: DateRange,
  } = {};
  isLoading = {
    pls: false,
    generate: false,
  };
  pls?: ProfitLossStatementModel;
  spendingEntries: DictionaryEntryModel[] = [];
  codeValueMap: Record<string, number> = {};

  private readonly incomeCodes = ['revenue_main_operation', 'other_income'];
  private readonly excludeFromExpenses = ['cogs', 'taxes_profit_category', 'revenue_main_operation', 'other_income'];

  private readonly reportService = inject(HxReportService);
  private readonly dictionaryService = inject(HxDictionaryService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly toastr = inject(HcToastrService);
  private readonly tr = inject(TranslocoService);

  constructor() {
    this.loadDictionary();
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = extractNumberArr('storeIds', paramMap);
      if (this.params.storeIds.length === 0) {
        this.params.storeIds = extractDefaultStoreIds();
      }
      if (this.params.storeIds.length > 0) {
        this.load();
      }
    });
  }

  getValueByCode(code: string): number {
    return this.codeValueMap[code] ?? 0;
  }

  getEntryTotal(entry: DictionaryEntryModel): number {
    if (!entry?.childEntries?.length) {
      return this.codeValueMap[entry.code] ?? 0;
    }
    return entry.childEntries.reduce((sum, child) => sum + (this.codeValueMap[child.code] ?? 0), 0);
  }

  get totalIncome(): number {
    return (this.codeValueMap['sale_finished_products'] ?? 0)
      + (this.codeValueMap['delivery_income'] ?? 0)
      + (this.codeValueMap['design_income'] ?? 0)
      + (this.codeValueMap['sale_extra_products'] ?? 0);
  }

  get incomeEntries(): DictionaryEntryModel[] {
    return this.spendingEntries.filter(e => this.incomeCodes.includes(e.code));
  }

  get expenseEntries(): DictionaryEntryModel[] {
    return this.spendingEntries.filter(e => !this.excludeFromExpenses.includes(e.code));
  }

  get totalExpenses(): number {
    if (!this.pls) {
      return 0;
    }
    return this.expenseEntries.reduce((sum, entry) => sum + this.getEntryTotal(entry), 0);
  }

  get costPrice(): number {
    const cogs = this.spendingEntries.find(e => e.code === 'cogs');
    return cogs ? this.getEntryTotal(cogs) : 0;
  }

  get grossProfit(): number {
    return this.totalIncome - this.costPrice;
  }

  get profitBeforeTax(): number {
    return this.grossProfit - this.totalExpenses;
  }

  get netProfit(): number {
    return this.profitBeforeTax - this.taxesProfit;
  }

  get taxesProfit(): number {
    const cat = this.spendingEntries.find(e => e.code === 'taxes_profit_category');
    return cat ? this.getEntryTotal(cat) : (this.codeValueMap['taxes_profit_category'] ?? 0);
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  resetFilter() {
    this.params.date = {from: isoDate(new Date()), to: isoDate(new Date())};
    this.navigate();
  }

  async generate() {
    this.isLoading.generate = true;
    try {
      const response = await this.reportService.generateProfitLossStatement({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
      });
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
    } catch (e: any) {
      console.log(e);
    } finally {
      this.isLoading.generate = false;
    }
  }

  private async loadDictionary() {
    this.spendingEntries = await this.dictionaryService.getEntries({ key: DictionaryKey.spendings, hierarchy: true });
  }

  private buildCodeValueMap() {
    if (!this.pls) {
      this.codeValueMap = {};
      return;
    }
    this.codeValueMap = { ...this.pls.categoryValues };
  }

  private async load() {
    this.pls = undefined;
    this.isLoading.pls = true;
    try {
      this.pls = await this.reportService.getProfitLossStatement({ fromDate: this.params.date.from, toDate: this.params.date.to, storeIds: this.params.storeIds });
      this.buildCodeValueMap();
    } finally {
      this.isLoading.pls = false;
    }
  }
}
